import React, { useState } from 'react';
import './FAQs.css';

/**
 * FAQs page component for JG Printing website.
 * Placeholder content — awaiting final copy from client.
 */

const faqData = [
    {
        category: 'Ordering',
        questions: [
            {
                question: 'How do I place an order?',
                answer: 'You can place an order through our website, Facebook page, or by visiting our store directly. Simply choose your product, provide your design file or let us know if you need design assistance, and we\'ll get started right away.'
            },
            {
                question: 'What file formats do you accept for printing?',
                answer: 'We accept PDF, PNG, JPG, JPEG, and AI (Adobe Illustrator) files. For best results, please submit files in high resolution (300 DPI or higher) with a CMYK color profile.'
            },
            {
                question: 'Can I request a sample or proof before full printing?',
                answer: 'Yes! We provide digital proofs for approval before we proceed with bulk printing. Physical samples may be available for certain product types — please contact us to inquire.'
            },
        ]
    },
    {
        category: 'Pricing & Payment',
        questions: [
            {
                question: 'How is pricing determined?',
                answer: 'Pricing depends on the product type, size, and quantity. Bulk orders typically enjoy lower per-unit rates. Feel free to message us for a custom quotation!'
            },
            {
                question: 'What payment methods do you accept?',
                answer: 'We accept GCash and Cash on Delivery (COD). Payment terms may vary depending on the order — we\'ll discuss this with you when you place your order.'
            },
            {
                question: 'Do you require a downpayment?',
                answer: 'Downpayment details are to be confirmed — please reach out to us directly for the most up-to-date payment terms for your order.'
            }
        ]
    },
    {
        category: 'Turnaround & Delivery',
        questions: [
            {
                question: 'How long does printing take?',
                answer: 'Standard turnaround is 3–5 business days depending on the product and quantity. Rush orders may be accommodated — please contact us in advance so we can check availability.'
            },
            {
                question: 'Do you offer delivery?',
                answer: 'Yes! We offer store pickup, rider delivery (Kuya), and J&T Express. Shipping fees may apply depending on your chosen delivery method.'
            },
            {
                question: 'What if my order arrives damaged?',
                answer: 'Please document the damage immediately upon receipt and contact us within 24 hours. We\'ll work with you to resolve the issue as quickly as possible.'
            }
        ]
    },
    {
        category: 'Design & Customization',
        questions: [
            {
                question: 'Do you offer design services?',
                answer: 'Yes! We have an in-house design team that can create or adjust layouts for an additional fee. Just let us know your vision and we\'ll make it happen.'
            },
            {
                question: 'Can I supply my own design?',
                answer: 'Absolutely. We love working with client-supplied designs. Just make sure your files meet our technical requirements (see file formats above) for the best print output.'
            },
        ]
    }
];

function FAQItem({ question, answer }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
            <button
                className="faq-question"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
            >
                <span>{question}</span>
                <span className="faq-icon">{isOpen ? '−' : '+'}</span>
            </button>
            <div className="faq-answer-wrapper">
                <p className="faq-answer">{answer}</p>
            </div>
        </div>
    );
}

function FAQs() {
    return (
        <main className="faqs-page">
            <section className="faqs-hero">
                <div className="faqs-hero-inner">
                    <p className="faqs-eyebrow">Got Questions?</p>
                    <h1 className="faqs-title">Frequently Asked<br />Questions</h1>
                    <p className="faqs-subtitle">
                        Everything you need to know about ordering, pricing, and our services.
                        Can't find your answer? <a href="#contact" className="faqs-contact-link">Reach out to us directly.</a>
                    </p>
                </div>
                <div className="faqs-hero-divider" />
            </section>

            <section className="faqs-body">
                <div className="faqs-container">
                    {faqData.map((section, sIndex) => (
                        <div key={sIndex} className="faq-category">
                            <div className="faq-category-header">
                                <span className="faq-category-number">0{sIndex + 1}</span>
                                <h2 className="faq-category-title">{section.category}</h2>
                            </div>
                            <div className="faq-list">
                                {section.questions.map((item, qIndex) => (
                                    <FAQItem
                                        key={qIndex}
                                        question={item.question}
                                        answer={item.answer}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="faqs-cta">
                <div className="faqs-cta-inner">
                    <p className="faqs-cta-label">Still have questions?</p>
                    <h2 className="faqs-cta-heading">We're happy to help.</h2>
                    <a
                        href="https://www.facebook.com/jgdigitalprinting"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="faqs-cta-button"
                    >
                        Message Us on Facebook
                    </a>
                </div>
            </section>
        </main>
    );
}

export default FAQs;